import type { RunStatus, Verdict } from './types.js';

// Named palette tokens map to the CSS custom properties defined in styles.css
// (--pill-<name>-bg / --pill-<name>-fg). Keeping the color as a token (rather
// than a raw hex) lets the stylesheet own light/dark presentation.
export type PillColor = 'amber' | 'blue' | 'cyan' | 'green' | 'magenta' | 'red' | 'grey';

export const STATUS_COLORS: Record<RunStatus, PillColor> = {
  QUEUED: 'amber',
  PROVISIONING: 'amber',
  RUNNING: 'cyan',
  SUCCESS: 'green',
  COMPLETED_NO_PR: 'blue',
  NEEDS_ITERATION: 'magenta',
  FAILED: 'red',
  TIMED_OUT: 'red',
  CANCELLED: 'grey',
};

export const VERDICT_COLORS: Record<Verdict, PillColor> = {
  READY: 'green',
  DEGRADED: 'amber',
  NOT_APPLICABLE: 'grey',
  UNKNOWN: 'grey',
};

// Secrets/overall verdicts share NOT_APPLICABLE/UNKNOWN with VERDICT_COLORS
// but add their own values (CONFIGURED/MISSING/NOT_READY) — kept as a
// separate map (rather than widening VERDICT_COLORS) since Verdict and
// SecretsVerdict/OverallVerdict are distinct types.
export const SECRETS_VERDICT_COLORS: Record<string, PillColor> = {
  CONFIGURED: 'green',
  MISSING: 'red',
  NOT_APPLICABLE: 'grey',
  UNKNOWN: 'grey',
  NOT_READY: 'red',
};

// Terminal statuses — stop polling logs / auto-refreshing once a run lands here.
export const TERMINAL_STATUSES: ReadonlySet<string> = new Set<RunStatus>([
  'SUCCESS',
  'COMPLETED_NO_PR',
  'NEEDS_ITERATION',
  'FAILED',
  'TIMED_OUT',
  'CANCELLED',
]);

// Active statuses — a run still in flight; used to decide whether to poll.
export const ACTIVE_STATUSES: ReadonlySet<string> = new Set<RunStatus>([
  'QUEUED',
  'PROVISIONING',
  'RUNNING',
]);

export function statusColor(status: string): PillColor {
  return STATUS_COLORS[status as RunStatus] ?? 'grey';
}

export function verdictColor(verdict: string): PillColor {
  return VERDICT_COLORS[verdict as Verdict] ?? SECRETS_VERDICT_COLORS[verdict] ?? 'grey';
}

export function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function isActive(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}
